import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../lib/i18n';
import { humanizeAuthError } from '../lib/authErrors';
import { CheckCircle2, AlertTriangle, Loader2, Pencil } from 'lucide-react';

/**
 * Single callback for all email-based auth flows:
 *   - Signup confirmation (type=signup)
 *   - Password recovery (type=recovery)  → /reset-password
 *   - Email change confirmation (type=email_change | email)
 *   - OAuth Google callback (no type, has code)
 *
 * Race-condition guard: pushes the session into React state synchronously
 * via setSessionFromExchange, then defers navigation by one tick so any
 * Protected route reads the updated `user` value.
 */
export default function AuthConfirm() {
  const { lang } = useT();
  const { setSessionFromExchange } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [state, setState] = useState('processing'); // processing | success | error
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function process() {
      const hashParams = new URLSearchParams(location.hash.replace(/^#/, ''));
      const queryParams = new URLSearchParams(location.search);

      // Surface error from Supabase if present (e.g., expired link)
      const errDesc = hashParams.get('error_description') || queryParams.get('error_description');
      if (errDesc) {
        if (!cancelled) {
          setError(humanizeAuthError({ message: decodeURIComponent(errDesc.replace(/\+/g, ' ')) }, lang));
          setState('error');
        }
        return;
      }

      // Detect flow type — Supabase email links carry ?type=recovery|email_change|signup|invite
      // Also honor next=/reset-password and a pending-reset flag set when requesting the email from the app.
      let pendingReset = false;
      try { pendingReset = localStorage.getItem('sp.pendingPasswordReset') === '1'; } catch { /* ignore */ }
      const type = queryParams.get('type') || hashParams.get('type') || '';
      const nextPath = queryParams.get('next') || '';
      const isRecovery =
        type === 'recovery' ||
        nextPath.includes('reset-password') ||
        pendingReset;
      const isEmailChange = type === 'email_change' || type === 'email';

      // PKCE flow: ?code=...
      const code = queryParams.get('code');
      // Modern OTP/token_hash flow: ?token_hash=...&type=...
      const tokenHash = queryParams.get('token_hash') || hashParams.get('token_hash');

      try {
        if (code) {
          const { error: exErr } = await supabase.auth.exchangeCodeForSession(code);
          if (exErr) throw exErr;
        } else if (tokenHash && (type || isRecovery)) {
          // Supabase verifyOtp accepts email|recovery|invite|signup|email_change types
          const otpType = isRecovery ? 'recovery' : isEmailChange ? 'email_change' : type;
          const { error: verErr } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: otpType,
          });
          if (verErr) throw verErr;
        }
      } catch (e) {
        if (!cancelled) {
          setError(humanizeAuthError(e, lang));
          setState('error');
        }
        return;
      }

      // Wait briefly for the hash-fragment flow (detectSessionInUrl) to settle
      await new Promise((r) => setTimeout(r, 400));

      const { data, error: sessErr } = await supabase.auth.getSession();
      if (cancelled) return;
      if (sessErr) {
        setError(humanizeAuthError(sessErr, lang));
        setState('error');
        return;
      }

      if (data.session) {
        // Push session synchronously into React state so the next route's
        // Protected wrapper / page reads the right user value.
        setSessionFromExchange(data.session);

        // Clean the URL of any sensitive query/hash params
        window.history.replaceState({}, '', '/auth/confirm');

        // Recovery → go to set-new-password page (now public, gated by user check)
        if (isRecovery) {
          try { localStorage.removeItem('sp.pendingPasswordReset'); } catch { /* ignore */ }
          // Defer to next tick so React flushes state before route eval
          setTimeout(() => { if (!cancelled) nav('/reset-password', { replace: true }); }, 0);
          return;
        }

        // Email change → confirm to user and send back to profile
        if (isEmailChange) {
          setState('email_changed');
          setTimeout(() => { if (!cancelled) nav('/profile', { replace: true }); }, 1800);
          return;
        }

        // Default (signup confirmation, OAuth, magic link)
        setState('success');
        setTimeout(() => { if (!cancelled) nav('/', { replace: true }); }, 1200);
        return;
      }

      // No session — link expired or already consumed
      setError(
        lang === 'fr'
          ? "Lien expiré ou déjà utilisé. Connectez-vous ou demandez un nouveau lien."
          : 'Link expired or already used. Sign in or request a new link.'
      );
      setState('error');
    }

    process();
    return () => { cancelled = true; };
  }, [location, nav, lang, setSessionFromExchange]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="px-4 sm:px-6 h-16 flex items-center gap-2">
        <div className="w-9 h-9 rounded-xl bg-[#7A1F2B] text-white flex items-center justify-center">
          <Pencil size={18} strokeWidth={2.4} />
        </div>
        <span className="font-heading font-black">SoumissionPro</span>
      </div>
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center" data-testid={`auth-confirm-${state}`}>
          {state === 'processing' && (
            <>
              <Loader2 className="mx-auto mb-6 animate-spin text-[#7A1F2B]" size={48} />
              <h1 className="font-heading text-3xl font-black tracking-tighter mb-2">
                {lang === 'fr' ? 'Confirmation en cours…' : 'Confirming…'}
              </h1>
              <p className="text-gray-500">
                {lang === 'fr' ? 'Quelques secondes, on vous connecte.' : 'A few seconds, signing you in.'}
              </p>
            </>
          )}
          {state === 'success' && (
            <>
              <div className="mx-auto mb-6 w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="text-emerald-600" size={36} />
              </div>
              <h1 className="font-heading text-3xl font-black tracking-tighter mb-2">
                {lang === 'fr' ? 'Compte confirmé !' : 'Account confirmed!'}
              </h1>
              <p className="text-gray-500">
                {lang === 'fr' ? 'Redirection vers votre tableau de bord…' : 'Redirecting to your dashboard…'}
              </p>
            </>
          )}
          {state === 'email_changed' && (
            <>
              <div className="mx-auto mb-6 w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="text-emerald-600" size={36} />
              </div>
              <h1 className="font-heading text-3xl font-black tracking-tighter mb-2">
                {lang === 'fr' ? 'Courriel mis à jour !' : 'Email updated!'}
              </h1>
              <p className="text-gray-500">
                {lang === 'fr' ? 'Redirection vers votre profil…' : 'Redirecting to your profile…'}
              </p>
            </>
          )}
          {state === 'error' && (
            <>
              <div className="mx-auto mb-6 w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="text-red-600" size={36} />
              </div>
              <h1 className="font-heading text-3xl font-black tracking-tighter mb-2">
                {lang === 'fr' ? 'Lien invalide' : 'Invalid link'}
              </h1>
              <p className="text-gray-600 mb-6">{error}</p>
              <button
                data-testid="confirm-back-to-login"
                onClick={() => nav('/auth', { replace: true })}
                className="btn-primary w-full"
              >
                {lang === 'fr' ? 'Retour à la connexion' : 'Back to sign in'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
